import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { IRemoteTileset, ISiteLocation } from '~/client/graph'
import Sitemap from '~/client/src/shared/models/Sitemap'
import Guard from '~/client/src/shared/utils/Guard'

import MapViewSetUpStore, { SetUpSteps } from '../MapViewSetUp.store'
import { ViewType } from './MapBoxEditor.store'

export default class SitemapControlStore {
  @observable public shouldShowEditBasemapMenu: boolean = false
  @observable public shouldShowCreateMapFileMenu: boolean = false
  @observable public shouldShowCreateWhiteboardMenu: boolean = false
  @observable public shouldShowCreateRemoteTilesetMenu: boolean = false
  @observable public shouldShowTooltipMenu: boolean = false
  @observable public whiteboardCreationPdfFile: File = null
  @observable public planCreationPdfFile: File = null
  @observable public planUpdatePdfFile: File = null
  @observable public location: ISiteLocation = null
  @observable public sitemapName: string = ''
  @observable public isUploading: boolean = false
  @observable public basemapFile: File = null
  @observable public tilesetFile: File = null

  public constructor(
    private readonly mapViewSetupStore: MapViewSetUpStore,
    private readonly setStep: (step: SetUpSteps) => void,
  ) {
    makeObservable(this)
    Guard.requireAll({
      mapViewSetupStore,
    })
  }

  public get selectedPdfFile(): File {
    return (
      this.whiteboardCreationPdfFile ||
      this.planCreationPdfFile ||
      this.planUpdatePdfFile
    )
  }

  @action.bound
  public async setConvertedBasemap(imgFile: Blob) {
    this.shouldShowTooltipMenu = true
    if (imgFile) {
      await this.updateBasemap(imgFile)
    }

    this.shouldShowTooltipMenu = false
  }

  @action.bound
  public applyLocation(location: ISiteLocation): void {
    this.location = location
  }

  @action.bound
  public changeMapName(event: React.ChangeEvent<HTMLInputElement>): void {
    this.sitemapName = event.currentTarget.value
  }

  @action.bound
  public async setConvertedPdf(imgFile: Blob): Promise<void> {
    if (this.whiteboardCreationPdfFile) {
      this.setConvertedPdfAsWhiteboard(imgFile)
    }
    if (this.planCreationPdfFile) {
      this.setConvertedPdfAsPlan(imgFile)
    }
    if (this.planUpdatePdfFile) {
      this.updateConvertedPdfPlan(imgFile)
    }
  }

  @action.bound
  public async updateConvertedPdfPlan(imgFile: Blob): Promise<void> {
    this.shouldShowEditBasemapMenu = true
    if (imgFile) {
      await this.updateBasemap(imgFile)
    }
    this.planUpdatePdfFile = null
    this.shouldShowEditBasemapMenu = false
  }

  @action.bound
  public async setConvertedPdfAsPlan(imgFile: Blob): Promise<void> {
    this.shouldShowCreateMapFileMenu = true

    if (imgFile) {
      await this.saveNewSitemap(imgFile, false, true)
    }

    this.planCreationPdfFile = null
    this.shouldShowCreateMapFileMenu = false
  }

  @action.bound
  public async setConvertedPdfAsWhiteboard(imgFile: Blob): Promise<void> {
    this.shouldShowCreateWhiteboardMenu = true
    if (imgFile) {
      await this.saveNewSitemap(imgFile, true)
    }

    this.whiteboardCreationPdfFile = null
    this.shouldShowCreateWhiteboardMenu = false
  }

  @action.bound
  public toggleTooltipMenu(): void {
    this.shouldShowTooltipMenu = !this.shouldShowTooltipMenu
    this.clearForm()
  }

  @action.bound
  public hideTooltipMenu(): void {
    this.shouldShowTooltipMenu = false
  }

  @action.bound
  public toggleCreateMapFileMenu(): void {
    this.shouldShowCreateMapFileMenu = !this.shouldShowCreateMapFileMenu
    if (this.shouldShowCreateWhiteboardMenu) {
      this.setStep(SetUpSteps.uploadPlan)
    } else {
      this.setStep(null)
    }
    this.clearForm()
  }

  @action.bound
  public toggleCreateWhiteboardMenu(): void {
    this.shouldShowCreateWhiteboardMenu = !this.shouldShowCreateWhiteboardMenu
    this.clearForm()
  }

  @action.bound
  public toggleCreateRemoteTilesetMenu(): void {
    this.shouldShowCreateRemoteTilesetMenu =
      !this.shouldShowCreateRemoteTilesetMenu
  }

  @action.bound
  public saveRemoteTileset(remoteTileset: IRemoteTileset): void {
    this.mapViewSetupStore.remoteTilesetsSetupStore.uploadRemoteTileset(
      remoteTileset,
    )
    this.shouldShowCreateRemoteTilesetMenu = false
  }

  @action.bound
  public hideEditMenu(): void {
    this.shouldShowEditBasemapMenu = false
    this.clearForm()
  }

  @action.bound
  public toggleEditMenu(): void {
    this.shouldShowEditBasemapMenu = !this.shouldShowEditBasemapMenu
    this.clearForm()
  }

  @action.bound
  public async setPdfOrUpdateBasemap(): Promise<void> {
    this.isUploading = true
    if (!this.basemapFile) {
      return
    }

    if (this.isPdfTypeBasemapSelected) {
      this.planUpdatePdfFile = this.basemapFile
      this.shouldShowEditBasemapMenu = false
      this.hideTooltipMenu()
      return
    }

    await this.updateBasemap(this.basemapFile)
  }

  @action.bound
  public async setPdfOrCreateSitemap(
    saveAsWhiteboard: boolean = false,
  ): Promise<void> {
    this.isUploading = true
    if (!this.basemapFile) {
      return
    }

    if (this.isPdfTypeBasemapSelected) {
      if (saveAsWhiteboard) {
        this.whiteboardCreationPdfFile = this.basemapFile
      } else {
        this.planCreationPdfFile = this.basemapFile
      }

      this.hideCreateMenu(false)
      return
    }
    await this.saveNewSitemap(this.basemapFile, saveAsWhiteboard)
    if (!saveAsWhiteboard) {
      this.setStep(SetUpSteps.alignPlan)
      this.mapViewSetupStore.mapBoxEditorStore.setLeftVisibility()
    }
  }

  @computed
  public get basmapFileObjectUrl(): string {
    return this.basemapFile ? URL.createObjectURL(this.basemapFile) : ''
  }

  public get isPdfTypeBasemapSelected(): boolean {
    return this.basemapFile?.type === 'application/pdf'
  }

  public showDeleteDialog = (): void => {
    const { showDeleteConfirmationDialogBySitemap, selectedSitemap } =
      this.mapViewSetupStore.sitemapsSetupStore
    showDeleteConfirmationDialogBySitemap(selectedSitemap)
  }

  @action.bound
  public async setSitemapBasemap(file: File): Promise<void> {
    runInAction(() => {
      this.isUploading = true
      this.basemapFile = file
    })
    runInAction(() => {
      this.isUploading = false
    })
  }

  @action.bound
  public setTileFile(file: File): void {
    this.tilesetFile = file
  }

  @action.bound
  public resetMapFIleSelection(): void {
    this.resetTileFile()
    this.resetBasemapFile()
  }

  @action.bound
  public resetBasemapFile(): void {
    this.basemapFile = null
  }

  @action.bound
  public resetTileFile(): void {
    this.tilesetFile = null
  }

  @action.bound
  public uploadTileFile(): void {
    const file = this.tilesetFile

    // Close dialog immediately - progress shown in Map Notifications
    this.toggleCreateMapFileMenu()
    this.resetTileFile()

    // Fire and forget - upload continues in background
    this.mapViewSetupStore.tilesetsSetupStore.uploadGeoTIFFToMapbox(file)
  }

  @action.bound
  private async saveNewSitemap(
    imgFile?: File | Blob,
    saveAsWhiteboard?: boolean,
    addAlligningStep?: boolean,
  ): Promise<void> {
    const sitemap =
      await this.mapViewSetupStore.sitemapsSetupStore.uploadNewSitemap(
        imgFile,
        this.sitemapName,
        this.basemapFile?.name,
      )
    if (saveAsWhiteboard) {
      this.mapViewSetupStore.globeViewSetupStore.deselectGlobe()
      this.mapViewSetupStore.onViewTypeSelect(ViewType.Objects)
    }
    await this.mapViewSetupStore.sitemapsSetupStore.selectSitemap(sitemap)
    runInAction(async () => {
      this.isUploading = false
    })
    if (addAlligningStep) {
      this.setStep(SetUpSteps.alignPlan)
      this.mapViewSetupStore.mapBoxEditorStore.setLeftVisibility()
    }
    if (saveAsWhiteboard) {
      this.updateAssociations(sitemap)
    }

    this.hideCreateMenu()
    this.clearForm()
  }

  private updateAssociations = (sitemap: Sitemap): void => {
    if (!this.location) {
      return
    }

    const { updateSitemapAssignedItems } =
      this.mapViewSetupStore.mapViewItemsSetupStore

    updateSitemapAssignedItems([this.location.id], sitemap)
  }

  @action.bound
  private hideCreateMenu(shouldClearForm: boolean = true): void {
    this.shouldShowCreateWhiteboardMenu = false
    this.shouldShowCreateMapFileMenu = false
    if (shouldClearForm) {
      this.clearForm()
    }
  }

  @action.bound
  private clearForm(): void {
    this.basemapFile = null
    this.whiteboardCreationPdfFile = null
    this.planCreationPdfFile = null
    this.planUpdatePdfFile = null
    this.tilesetFile = null
    this.isUploading = false
    this.location = null
    this.sitemapName = ''
  }

  @action.bound
  private async updateBasemap(imgFile?: File | Blob): Promise<void> {
    const { selectedSitemap, updateBasemapForSitemap } =
      this.mapViewSetupStore.sitemapsSetupStore

    await updateBasemapForSitemap(imgFile || this.basemapFile, selectedSitemap)

    this.isUploading = false
    this.hideEditMenu()
  }
}
